/**
 * Composable for role-based permission checks
 */

import { computed } from 'vue';
import { useStore } from 'vuex';
import { isRouteAccessible } from '@/config/navigation';

export function usePermissions() {
  const store = useStore();
  
  // Get user roles from store
  const userRoles = computed(() => store.getters.userRoles || []);
  
  // Check if user is authenticated
  const isAuthenticated = computed(() => store.getters.isAuthenticated);
  
  // Check if auth is initialized
  const isAuthInitialized = computed(() => store.getters.isAuthInitialized);
  
  // Check if user is admin
  const isAdmin = computed(() => store.getters.isAdmin);
  
  /**
   * Check if user has specific role
   * @param {string} role - Role to check
   * @returns {boolean}
   */
  function hasRole(role) {
    return userRoles.value.includes(role);
  }
  
  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  function hasAnyRole(roles) {
    if (!roles || roles.length === 0) return true;
    return roles.some(role => hasRole(role));
  }
  
  /**
   * Check if user has all of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  function hasAllRoles(roles) {
    if (!roles || roles.length === 0) return true;
    return roles.every(role => hasRole(role));
  }
  
  /**
   * Check if user can access specific route
   * @param {string} route - Route path to check
   * @returns {boolean}
   */
  function canAccessRoute(route) {
    if (!isAuthenticated.value) return false;
    return isRouteAccessible(route, userRoles.value);
  }
  
  /**
   * Check if user can perform an action based on permission requirements
   * @param {object} requirements - Permission requirements object
   * @param {boolean} requirements.requiresAuth - Requires authentication
   * @param {string[]} requirements.requiresRoles - Required roles
   * @param {string[]} requirements.requiresAnyRole - Requires any of these roles
   * @param {string[]} requirements.requiresAllRoles - Requires all of these roles
   * @returns {boolean}
   */
  function canPerformAction(requirements = {}) {
    // Check authentication requirement
    if (requirements.requiresAuth && !isAuthenticated.value) {
      return false;
    }
    
    // Check specific roles
    if (requirements.requiresRoles) {
      return hasAnyRole(requirements.requiresRoles);
    }
    
    // Check any role requirement
    if (requirements.requiresAnyRole) {
      return hasAnyRole(requirements.requiresAnyRole);
    }
    
    // Check all roles requirement
    if (requirements.requiresAllRoles) {
      return hasAllRoles(requirements.requiresAllRoles);
    }
    
    return true;
  }
  
  /**
   * Wait for auth initialization
   * @returns {Promise<void>}
   */
  async function waitForAuth() {
    if (isAuthInitialized.value) return;
    
    return new Promise((resolve) => {
      const unwatch = store.watch(
        (state) => state.authInitialized,
        (authInitialized) => {
          if (authInitialized) {
            unwatch();
            resolve();
          }
        }
      );
    });
  }
  
  return {
    // Computed properties
    userRoles,
    isAuthenticated,
    isAuthInitialized,
    isAdmin,
    
    // Methods
    hasRole,
    hasAnyRole,
    hasAllRoles,
    canAccessRoute,
    canPerformAction,
    waitForAuth
  };
}